import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GridLogicOperator } from '@mui/x-data-grid-pro';
import { unauthorize } from './Security/authorizationSlice';
import { Terminal } from '@api/Trinium/types';
import { defaultAllOpenQuery } from '@api/Trinium/Endpoints/BoardQueries/types';

export const DEFAULT_DOCUMENT_TITLE = 'Dispatch Portal';

interface ApplicationState {
	/** HTML Page Title */
	pageTitle: string;
	/** Currently active/selected Terminal for the Application */
	selectedTerminal: Terminal | null;
	/** */
	selectedBoardQueryName: string;
	/**
	 * Snapshot of the current Dispatch Board URL used for navigating back to the Board when leaving the route
	 */
	dispatchPathSnapshot?: string;
	/** Quick Filter logic operator preference for the Board DataGrid */
	quickFilterLogicOperator: GridLogicOperator;
}

const initialState: ApplicationState = {
	pageTitle: DEFAULT_DOCUMENT_TITLE,
	selectedTerminal: null,
	selectedBoardQueryName: defaultAllOpenQuery.name,
	quickFilterLogicOperator: GridLogicOperator.And
};

/**
 * Application-wide state that may need to be accessed such as the currently selected terminal.
 */
const applicationSlice = createSlice({
	name: 'applicationState',
	initialState,
	reducers: {
		setPageTitle(state, action: PayloadAction<string>) {
			return {
				...state,
				pageTitle: action.payload
			};
		},
		setTerminal(state, action: PayloadAction<Terminal | null>) {
			return {
				...state,
				selectedTerminal: action.payload
			};
		},
		setCurrentQuery(state, action: PayloadAction<string>) {
			return {
				...state,
				selectedBoardQueryName: action.payload
			};
		},
		setDispatchSnapshot(state, action: PayloadAction<string | undefined>) {
			return {
				...state,
				dispatchPathSnapshot: action.payload
			};
		},
		setQuickFilterLogicOperator(state, action: PayloadAction<GridLogicOperator>) {
			return {
				...state,
				quickFilterLogicOperator: action.payload
			};
		},
		// Action restores initial state of this reducer, but other reducers are watching for this action as well
		// and act on their own state if they need to.
		resetStore() {
			return {
				...initialState
			};
		}
	},
	extraReducers: (builder) => {
		builder.addCase(unauthorize, () => {
			return initialState;
		});
	}
});

export const { setPageTitle, setCurrentQuery, setDispatchSnapshot, setQuickFilterLogicOperator, setTerminal, resetStore } = applicationSlice.actions;

export default applicationSlice;
